import { styled } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';

export const EmptyPortContainer = styled(Stack)`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.palette.grey['04']};
`;

export const PortDetailsContainer = styled(Stack)`
  width: 100%;
  height: 100%;
  gap: 20px;
  padding: 0;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  /* Chrome, Edge, Safari */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

export const PortHeader = styled(Stack)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px 20px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.grey['02']} 0%,
    ${({ theme }) => theme.palette.primary.main}08 100%
  );
  border: 1px solid ${({ theme }) => theme.palette.primary.main}20;
  border-radius: 10px;
  gap: 16px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px ${({ theme }) => theme.palette.primary.main}08;
`;

export const StatusBadge = styled(Box, {
  shouldForwardProp: (prop) => !['status', 'enabled'].includes(prop as string),
})<{ status: 'connected' | 'disconnected' | 'duplex'; enabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ theme, status }) => {
    if (status === 'CONNECTED') return `${theme.palette.primary.main}`;
    if (status === 'DISCONNECTED') return `${theme.palette.error.main}`;
    if (status === 'DISABLED') return `${theme.palette.grey['02']}`;
    if (theme.palette.mode === 'dark') {
      return `${theme.palette.info.main}`;
    }
    return `${theme.palette.primary.main}12`;
  }};
  color: ${({ theme, status }) => {
    if (status === 'CONNECTED') return theme.palette.fixed.black;
    if (status === 'DISCONNECTED') return theme.palette.error.main;
    if (status === 'DISABLED') return theme.palette.fixed.main;
    if (theme.palette.mode === 'dark') {
      return theme.palette.info.main;
    }
    return theme.palette.primary.main;
  }} !important;
  border: 1.5px solid
    ${({ theme, status }) => {
      if (status === 'connected') return `${theme.palette.success.main}30`;
      if (status === 'disconnected') return `${theme.palette.error.main}30`;
      if (theme.palette.mode === 'dark') {
        return `${theme.palette.info.main}40`;
      }
      return `${theme.palette.primary.main}30`;
    }};
  box-shadow: 0 2px 6px
    ${({ theme, status }) => {
      if (status === 'connected') return `${theme.palette.success.main}10`;
      if (status === 'disconnected') return `${theme.palette.error.main}10`;
      if (theme.palette.mode === 'dark') {
        return `${theme.palette.info.main}15`;
      }
      return `${theme.palette.primary.main}10`;
    }};
`;

export const BytesComparisonContainer = styled(Box)`
  padding: 16px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary['03']},
    transparent
  );
  border: 1px solid ${({ theme }) => theme.palette.primary.main}15;
  border-radius: 10px;
  flex-shrink: 0;
  box-shadow: 0 2px 6px ${({ theme }) => theme.palette.primary.main}06;
`;

export const BytesBar = styled(Box)`
  display: flex;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow:
    0 2px 6px ${({ theme }) => theme.palette.primary.main}12,
    inset 0 1px 1px rgba(255, 255, 255, 0.2);
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main}02 0%,
    ${({ theme }) => theme.palette.primary.main}06 100%
  );
`;

export const BytesSentBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'percentage',
})<{ percentage: number }>`
  flex: ${({ percentage }) => percentage};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary['06']} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 13px;
  transition: all 0.3s ease;
  min-width: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 100%
    );
    pointer-events: none;
  }

  &:hover {
    filter: brightness(1.15);
  }
`;

export const BytesReceivedBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'percentage',
})<{ percentage: number }>`
  flex: ${({ percentage }) => percentage};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.primary.main} 100%,
    ${({ theme }) => theme.palette.primary['04']} 0%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 13px;
  transition: all 0.3s ease;
  min-width: 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.2) 0%,
      transparent 100%
    );
    pointer-events: none;
  }

  &:hover {
    filter: brightness(1.15);
  }
`;

export const DataGridContainer = styled(Stack)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 14px;
  width: 100%;
  flex: 1;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 16px;
  }
`;

export const DataGridItem = styled(Stack)`
  padding: 14px 10px;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid ${({ theme }) => theme.palette.divider};
  border-radius: 0;
  gap: 16px;
  align-items: flex-start;
  justify-content: flex-start;
  padding-bottom: 4px;
  transition: all 0.3s ease;
  min-height: 0;

  &:hover {
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.palette.primary.main}04 0%,
      transparent 100%
    );
  }
`;

export const DataGridLabel = styled(Typography)`
  font-size: 10px !important;
  font-weight: 700 !important;
  color: ${({ theme }) => theme.palette.grey['01']};
  text-transform: uppercase;
  text-align: left;
  letter-spacing: 0.5px;
  line-height: 1.1;
  height: 20px;
`;

export const DataGridValue = styled(Typography)`
  font-size: 17px !important;
  font-weight: 700 !important;
  color: ${({ theme }) => theme.palette.text.primary};
  line-height: 1.3;
`;

export const AvailablePortsLabel = styled(Typography)`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.palette.grey['01']};
`;

export const PortsListStack = styled(Stack)`
  flex-direction: row;
  gap: 12px;
  flex-wrap: wrap;
  align-items: stretch;
  align-content: flex-start;
  max-height: 400px;
  overflow-y: auto;
  justify-content: center;
`;

/** Separates LAN from PON ports inside the wrapping port row */
export const PortsDivider = styled(Divider)`
  align-self: stretch;
  height: auto;
  border-style: dashed;
  border-right-width: 2px;
`;

export const DataTransferTitle = styled(Typography)`
  font-weight: 700;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const BytesStatsRow = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
`;

export const TransferStatLabel = styled(Typography)`
  color: ${({ theme }) => theme.palette.grey['03']};
  font-size: 11px;
  font-weight: 500;
`;

export const TransferStatValue = styled(Typography)`
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 13px;
  line-height: 1.3;
`;

export const ConnectedStatusBadgeText = styled(Typography)`
  font-weight: 600;
`;

export const DisconnectedStatusBadgeText = styled(Typography)`
  font-weight: 600;
  color: ${({ theme }) => theme.palette.fixed.white};
`;

export const DuplexStatusBadgeText = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.fixed.black};
`;
